import { FormEvent, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

const LoginPage = () => {
  const { login, isAuthenticated, loading, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  useEffect(() => {
    const stateError = (location.state as { error?: string } | null)?.error;
    const qsError = searchParams.get("error");
    const effectiveError = stateError || qsError;
    if (effectiveError) {
      setLocalError(effectiveError);
      toast({ title: "Acesso negado", description: effectiveError, variant: "destructive" });
    }
  }, [location.state, searchParams, toast]);

  useEffect(() => {
    if (!isAuthenticated || loading) return;
    const next = searchParams.get("next");
    if (next) {
      navigate(next, { replace: true });
    } else {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, loading, navigate, searchParams]);

  const handleAuth = async () => {
    setLocalError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      toast({ title: "Login realizado", description: "Bem-vindo de volta!" });
      const next = searchParams.get("next");
      navigate(next || "/", { replace: true });
    } catch (authError) {
      const message =
        (authError as Error)?.message ||
        error ||
        "Não foi possível autenticar. Tente novamente.";
      setLocalError(message);
      toast({ title: "Erro", description: message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await handleAuth();
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-muted-foreground">Carregando sessão...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8 space-y-2">
          <h1 className="text-3xl font-bold text-foreground">Portal de Acesso</h1>
          <p className="text-sm text-muted-foreground">
            Acesse sua conta Ferroz Tracking para continuar
          </p>
          <p className="text-xs text-muted-foreground">Entre com seu e-mail e senha para acessar o painel.</p>
        </div>

        <Card className="shadow-elegant border border-border/70">
          <CardHeader className="space-y-1 text-center">
            <CardTitle className="text-2xl">Entrar</CardTitle>
            <CardDescription>Use suas credenciais para acessar o painel</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="voce@ferroztracking.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Sua senha"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-3 flex items-center text-muted-foreground hover:text-foreground"
                    aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {(localError || error) && (
                <div
                  role="alert"
                  className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2"
                >
                  {localError || error}
                </div>
              )}

              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? "Entrando..." : "Entrar"}
              </Button>
            </form>
            <div className="mt-4 text-center text-sm text-muted-foreground">
              <span className="mr-2">Não tem uma conta?</span>
              <Link to="/sign-up" className="font-semibold text-primary hover:underline">
                Criar conta
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;

